import React, { useEffect, useState } from "react";
import AdminHead from "./AdminHead";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../Components/Firebase";
import emailjs from "emailjs-com";
import Loading from "../Components/Loading";
import "../Styles/Admin/ViewOrder.css";

const EMAILJS_SERVICE_ID = "service_46i5m6n";
const EMAILJS_TEMPLATE_ID = "template_qjuypdg";
const EMAILJS_USER_ID = "ZmqOOthrLXBiLiqZ6";

function ViewQuotation() {
  const { QUID } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalImage, setModalImage] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [itemPrices, setItemPrices] = useState({});

  useEffect(() => {
    const fetchOrderAndNames = async () => {
      try {
        const orderRef = doc(db, "Quotations", QUID);
        const orderSnap = await getDoc(orderRef);

        if (!orderSnap.exists()) {
          setLoading(false);
          return;
        }

        const orderData = orderSnap.data();
        setOrder(orderData);

        // Initialize prices from existing data
        if (orderData.items) {
          const prices = {};
          orderData.items.forEach((item, index) => {
            prices[index] = item.price || 0;
          });
          setItemPrices(prices);
        }

        if (orderData.CustomerID) {
          const customerRef = doc(db, "Customers", orderData.CustomerID);
          const customerSnap = await getDoc(customerRef);
          if (customerSnap.exists()) {
            setCustomerName(customerSnap.data().Name || "");
          } else {
            setCustomerName("לא נמצא לקוח");
          }
        }

        if (orderData.SapakId) {
          const supplierRef = doc(db, "Users", orderData.SapakId);
          const supplierSnap = await getDoc(supplierRef);
          if (supplierSnap.exists()) {
            const data = supplierSnap.data();
            setSupplierName(data.Name || "");
            setSupplierEmail(data.Email || "");
          } else {
            setSupplierName("לא נמצא ספק");
          }
        }
      } catch (error) {
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderAndNames();
  }, [QUID]);

  const openModal = (url) => setModalImage(url);
  const closeModal = () => setModalImage(null);

  const handlePriceChange = (index, value) => {
    const numericValue = parseFloat(value) || 0;
    setItemPrices(prev => ({
      ...prev,
      [index]: numericValue
    }));
  };

  const savePrices = async () => {
    if (!order) return;
    try {
      const updatedItems = order.items.map((item, index) => ({
        ...item,
        price: itemPrices[index] || 0
      }));

      const orderRef = doc(db, "Quotations", QUID);
      await updateDoc(orderRef, { items: updatedItems });
      
      setOrder(prev => ({ ...prev, items: updatedItems }));

      const templateParams = {
        name: supplierName,
        email: supplierEmail || "default@supplier.com",
        orderNumber: order.orderNumber,
        totalPrice: calculateTotal().toLocaleString(),
        Date: order.createdAt?.toDate().toLocaleDateString() || "",
      };

      emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_USER_ID
      ).then(() => {
        console.log("Email sent to supplier.");
      }).catch((err) => {
        console.error("Error sending email:", err);
      });
      
      alert("מחירים עודכנו בהצלחה");
    } catch (error) {
      alert("שגיאה בעדכון המחירים");
    }
  };

  const calculateTotal = () => {
    return Object.values(itemPrices).reduce((sum, price) => sum + (parseFloat(price) || 0), 0);
  };

  const calculateVAT = () => {
    const total = calculateTotal();
    return total * (18 / 118); // VAT portion from total price (18/118 = VAT rate / (1 + VAT rate))
  };

  const calculateSubtotal = () => {
    return calculateTotal() - calculateVAT();
  };

  const getStatusText = (status) => {
    switch ((status || "").toLowerCase()) {
      case "pending":
        return "בהמתנה";
      case "approved":
        return "מאושר";
      case "rejected":
        return "נדחה";
      default:
        return "לא ידוע";
    }
  };

  const getStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "pending":
        return "#ff9800";
      case "approved":
        return "#4caf50";
      case "rejected":
        return "#f44336";
      default:
        return "#9e9e9e";
    }
  };

  const handlePrint = () => {
    if (!order) return;

    const typeHebrewMap = {
      Upholsterer: "ריפוד",
      Carpentry: "נגרות",
      sofa: "ספה",
      bed: "מיטה",
      closet: "ארון",
    };

    const total = calculateTotal();
    const vat = calculateVAT();
    const subtotal = calculateSubtotal();
    const itemCount = order.items ? order.items.length : 0;

    const htmlContent = `
<html dir="rtl">
<head>
  <title>הדפסת הזמנה</title>
  <style>
    body {
      font-family: 'Arial', sans-serif;
      direction: rtl;
      background-color: #fff;
      color: #000;
      margin: 20px;
    }
    h1, h2 {
      border-bottom: 2px solid #333;
      padding-bottom: 5px;
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 30px;
    }
    .logo {
      max-height: 150px;
    }
    .order-details {
      margin-bottom: 20px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      page-break-inside: auto;
    }
    th, td {
      text-align: right;
      border: 1px solid #ccc;
      padding: 10px;
      vertical-align: top;
      word-break: break-word;
      white-space: normal;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    .full-width-row td {
      background: #f9f9f9;
    }
    img {
      max-width: 80px;
      max-height: 80px;
      margin: 4px;
    }
    .shape-img {
      max-width: 100px;
      border: 1px solid #ccc;
      margin-bottom: 10px;
    }
    .signature-section {
      margin-top: 30px;
      border: 2px solid #333;
      padding: 20px;
      background: #f9f9f9;
    }
    .signature-img {
      max-width: 200px;
      max-height: 100px;
      border: 1px solid #ddd;
      border-radius: 4px;
      margin-top: 10px;
    }
    .pricing-summary {
      margin-top: 30px;
      border: 2px solid #333;
      padding: 20px;
      background: #f9f9f9;
    }
    .pricing-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      border-bottom: 2px solid #333;
      padding-bottom: 10px;
    }
    .pricing-header h2 {
      margin: 0;
      border: none;
      padding: 0;
    }
    .order-info {
      font-size: 14px;
      color: #666;
    }
    .pricing-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      font-size: 16px;
    }
    .total-row {
      font-weight: bold;
      font-size: 18px;
      border-top: 2px solid #333;
      padding-top: 10px;
    }
    .page-break {
      page-break-after: always;
      margin-bottom: 40px;
    }
    @media print {
      body {
        margin: 0;
      }
      .header {
        page-break-inside: avoid;
      }
      .page-break {
        page-break-after: always;
      }
    }
  </style>
</head>
<body>
  ${order.items.map((item, index) => `
    <div class="page-break">
      <div class="header">
        <div>
          <h1>הצעת מחיר מספר ${order.orderNumber} - פריט ${index + 1}</h1>
        </div>
        <div>
          <img src="https://firebasestorage.googleapis.com/v0/b/milaaz-4538e.firebasestorage.app/o/Logo%2FMilaaz1.png?alt=media&token=24f09477-3d96-4419-af28-4978ceb04997" alt="Logo" class="logo" />
        </div>
      </div>

      <div class="order-details">
        ${supplierName ? `<p><strong>שם סוכן:</strong> ${supplierName}</p>` : ""}
        ${customerName ? `<p><strong>שם לקוח:</strong> ${customerName}</p>` : ""}
        ${order.createdAt ? `<p><strong>תאריך יצירה:</strong> ${order.createdAt.toDate().toLocaleString()}</p>` : ""}
        <p><strong>סטטוס:</strong> ${getStatusText(order.QStatus)}</p>
        ${order.approvalDate ? `<p><strong>תאריך אישור:</strong> ${new Date(order.approvalDate.seconds * 1000).toLocaleString()}</p>` : ""}
      </div>

      <h2>פרטי מוצר</h2>
      <table>
        <thead>
          <tr>
            <th>מס'</th>
            ${item.name ? "<th>שם</th>" : ""}
            ${item.type ? "<th>סוג</th>" : ""}
            ${item.fabricManufacturer ? "<th>חברה</th>" : ""}
            ${item.fabricCode ? "<th>מספר חומר</th>" : ""}
            ${item.width ? "<th>רוחב 1</th>" : ""}
            ${item.width2 ? "<th>רוחב 2</th>" : ""}
            ${item.width3 ? "<th>רוחב 3</th>" : ""}
            ${item.height ? "<th>גובה</th>" : ""}
            ${item.depth ? "<th>עומק</th>" : ""}
            <th>מחיר</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            ${item.name ? `<td>${item.name}</td>` : ""}
            ${item.type ? `<td>${typeHebrewMap[item.type] || item.type}</td>` : ""}
            ${item.fabricManufacturer ? `<td>${item.fabricManufacturer}</td>` : ""}
            ${item.fabricCode ? `<td>${item.fabricCode}</td>` : ""}
            ${item.width ? `<td>${item.width} ס״מ</td>` : ""}
            ${item.width2 ? `<td>${item.width2} ס״מ</td>` : ""}
            ${item.width3 ? `<td>${item.width3} ס״מ</td>` : ""}
            ${item.height ? `<td>${item.height} ס״מ</td>` : ""}
            ${item.depth ? `<td>${item.depth} ס״מ</td>` : ""}
            <td>₪${(itemPrices[index] || 0).toLocaleString()}</td>
          </tr>
          <tr class="full-width-row">
            <td colspan="${3 
              + (item.name ? 1 : 0) 
              + (item.type ? 1 : 0) 
              + (item.fabricManufacturer ? 1 : 0) 
              + (item.fabricCode ? 1 : 0) 
              + (item.width ? 1 : 0) 
              + (item.width2 ? 1 : 0) 
              + (item.width3 ? 1 : 0) 
              + (item.height ? 1 : 0) 
              + (item.depth ? 1 : 0)
            }">
              ${item.description ? `<p><strong>תיאור:</strong> ${item.description}</p>` : ""}
              ${item.woodType ? `<p><strong>סוג עץ:</strong> ${item.woodType}</p>` : ""}
              ${item.notes ? `<p><strong>הערות:</strong> ${item.notes}</p>` : ""}
              ${item.deliveryDate ? `<p><strong>תאריך אספקה:</strong> ${new Date(item.deliveryDate).toLocaleDateString()}</p>` : ""}
              ${item.shapeUrl ? `<div><strong>צורת פריט:</strong><br><img src="${item.shapeUrl}" alt="צורת פריט" class="shape-img" /></div>` : ""}
              ${item.shape2Url ? `<div><strong>צורת פריט 2:</strong><br><img src="${item.shape2Url}" alt="צורת פריט 2" class="shape-img" /></div>` : ""}
              <div>${(item.images || []).map(url => `<img src="${url}" alt="תמונה" />`).join("")}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `).join("")}

  <div class="pricing-summary">
    <div class="pricing-header">
      <h2>סיכום מחירים</h2>
      <div class="order-info">
        <div>הצעת מחיר מספר: ${order.orderNumber}</div>
        <div>כמות פריטים: ${itemCount}</div>
        <div>סטטוס: ${getStatusText(order.QStatus)}</div>
      </div>
    </div>
    <div class="pricing-row total-row">
      <span>סה"כ לתשלום:</span>
      <span>₪${total.toLocaleString()}</span>
    </div>
    <div class="pricing-row">
      <span>כולל מע"מ (18%):</span>
      <span>₪${vat.toLocaleString()}</span>
    </div>
    <div class="pricing-row">
      <span>ללא מע"מ:</span>
      <span>₪${subtotal.toLocaleString()}</span>
    </div>
  </div>

  ${order.signature && order.QStatus === "approved" ? `
    <div class="signature-section">
      <h2>חתימת אישור</h2>
      <p><strong>נחתם על ידי:</strong> ${supplierName}</p>
      ${order.approvalDate ? `<p><strong>תאריך חתימה:</strong> ${new Date(order.approvalDate.seconds * 1000).toLocaleString()}</p>` : ""}
      <img src="${order.signature}" alt="חתימה דיגיטלית" class="signature-img" />
    </div>
  ` : ""}

  <script>
    window.onload = function() {
      window.print();
      setTimeout(() => window.close(), 1000);
    };
  </script>
</body>
</html>
`;

    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  if (loading) return <Loading />;
  if (!order) return <p dir="rtl">לא נמצאה הזמנה להצגה.</p>;

  return (
    <>
      <AdminHead />
      <div className="view-order-container" dir="rtl" style={{ padding: "20px" }}>
        <h1>פרטי הצעת מחיר</h1>
        <p><strong>מספר הצעת מחיר:</strong> {order.orderNumber}</p>
        <p><strong>תאריך יצירה:</strong> {order.createdAt?.toDate().toLocaleString()}</p>
        <p><strong>שם סוכן:</strong><em> {supplierName}</em></p>
        <p><strong>שם לקוח:</strong><em> {customerName}</em></p>
        
        {/* Status Display */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          marginBottom: "20px",
          padding: "10px",
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "8px"
        }}>
          <strong style={{ marginLeft: "10px" }}>סטטוס הצעת מחיר:</strong>
          <span style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "20px",
            color: "white",
            fontWeight: "bold",
            backgroundColor: getStatusColor(order.QStatus)
          }}>
            {getStatusText(order.QStatus)}
          </span>
          {order.approvalDate && (
            <span style={{ marginRight: "15px", color: "#666", fontSize: "14px" }}>
              תאריך אישור: {new Date(order.approvalDate.seconds * 1000).toLocaleString()}
            </span>
          )}
        </div>

        {/* Digital Signature Display */}
        {order.signature && order.QStatus === "approved" && (
          <div style={{
            marginBottom: "30px",
            padding: "20px",
            border: "2px solid #28a745",
            backgroundColor: "#f8fff9",
            borderRadius: "8px"
          }}>
            <h3 style={{ color: "#28a745", marginBottom: "15px" }}>
              ✓ הצעת מחיר אושרה ונחתמה
            </h3>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
              <strong style={{ marginLeft: "10px" }}>נחתם על ידי:</strong>
              <span>{supplierName}</span>
            </div>
            {order.approvalDate && (
              <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                <strong style={{ marginLeft: "10px" }}>תאריך חתימה:</strong>
                <span>{new Date(order.approvalDate.seconds * 1000).toLocaleString()}</span>
              </div>
            )}
            <div>
              <strong style={{ display: "block", marginBottom: "10px" }}>חתימה דיגיטלית:</strong>
              <img 
                src={order.signature} 
                alt="חתימה דיגיטלית" 
                style={{
                  maxWidth: "250px",
                  maxHeight: "120px",
                  border: "2px solid #28a745",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  padding: "10px",
                  cursor: "pointer"
                }}
                onClick={() => openModal(order.signature)}
                title="לחץ לצפייה בחתימה מוגדלת"
              />
            </div>
          </div>
        )}

        <button onClick={handlePrint} className="print-order-btn">הדפס הצעה</button>
        <button onClick={savePrices} className="save-status-btn" style={{ marginRight: 10 }}>שמור מחירים</button>

        {order.items && order.items.map((item, index) => (
          <div key={index} className="sview-item">
            {item.name && <p><span className="sview-label">שם פריט:</span> {item.name}</p>}
            {item.description && <p><span className="sview-label">תיאור:</span> {item.description}</p>}
            {item.type && <p><span className="sview-label">סוג:</span> {item.type}</p>}
            {item.fabricCode && <p><span className="sview-label">קוד בד:</span> {item.fabricCode}</p>}
            {item.fabricManufacturer && <p><span className="sview-label">יצרן בד:</span> {item.fabricManufacturer}</p>}
            {item.woodType && <p><span className="sview-label">סוג עץ:</span> {item.woodType}</p>}
            {item.width !== undefined && item.width !== "" && <p><span className="sview-label">רוחב 1:</span> {item.width} ס״מ</p>}
            {item.width2 !== undefined && item.width2 !== "" && <p><span className="sview-label">רוחב 2:</span> {item.width2} ס״מ </p>}
            {item.width3 !== undefined && item.width3 !== "" && <p><span className="sview-label">רוחב 3:</span> {item.width3} ס״מ</p>}
            {item.height !== undefined && item.height !== "" && <p><span className="sview-label">גובה:</span> {item.height} ס״מ</p>}
            {item.depth !== undefined && item.depth !== "" && <p><span className="sview-label">עומק:</span> {item.depth} ס״מ</p>}
            {item.notes && <p><span className="sview-label">הערות:</span> {item.notes}</p>}
            {item.deliveryDate && <p><span className="sview-label">תאריך אספקה:</span> {new Date(item.deliveryDate).toLocaleDateString()}</p>}

            <div style={{ marginTop: "15px", marginBottom: "15px" }}>
              <span className="sview-label">מחיר:</span>
              <input
                type="number"
                value={itemPrices[index] || ""}
                onChange={(e) => handlePriceChange(index, e.target.value)}
                placeholder="הכנס מחיר"
                style={{
                  marginRight: "10px",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  width: "150px"
                }}
              />
              ₪
            </div>

            {item.shapeUrl && (
              <div className="sview-shape">
                <p><strong>צורת פריט:</strong></p>
                <img src={item.shapeUrl} alt="צורת פריט" style={{ maxWidth: "100px", border: "1px solid #ccc", marginBottom: "10px" }} />
              </div>
            )}

            {item.shape2Url && (
              <div className="sview-shape">
                <p><strong>צורת פריט 2:</strong></p>
                <img src={item.shape2Url} alt="צורת פריט 2" style={{ maxWidth: "100px", border: "1px solid #ccc", marginBottom: "10px" }} />
              </div>
            )}

            {item.images && item.images.length > 0 && (
              <div className="sview-images">
                {item.images.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`תמונה ${i + 1} של פריט ${index + 1}`}
                    onClick={() => openModal(url)}
                    style={{ cursor: "pointer", maxWidth: "100px", marginRight: "8px" }}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Pricing Summary */}
        <div style={{
          marginTop: "30px",
          padding: "20px",
          border: "2px solid #333",
          backgroundColor: "#f9f9f9",
          borderRadius: "8px"
        }}>
          <h2>סיכום מחירים</h2>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "15px",
            borderBottom: "2px solid #333",
            paddingBottom: "10px"
          }}>
            <span>סה"כ לתשלום:</span>
            <span>₪{calculateTotal().toLocaleString()}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px", fontSize: "16px" }}>
            <span>מע"מ (18%):</span>
            <span>₪{calculateVAT().toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* ✅ FIXED MODAL */}
      {modalImage && (
        <div className="modal-backdrop" onClick={closeModal}>
          {/* stop click bubbling so image click doesn't close instantly */}
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={modalImage} alt="תמונה מוגדלת" />
          </div>
        </div>
      )}
    </>
  );
}

export default ViewQuotation;